import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import type { TaskListSectionModel } from '@domain-group/realhub-backend-api-types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, ActionResult, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/taskListSection/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/taskListSection/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/taskListSection/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/taskListSection/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/taskListSection/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/taskListSection/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/taskListSection/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/taskListSection/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/taskListSection/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/taskListSection/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/taskListSection/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/taskListSection/DELETE_FAILURE'

export type TaskListSectionModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: TaskListSectionModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedIds: [],
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}): Action {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess(): Action {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []): Action {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(): Action {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(): Action {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []): Action {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(): Action {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(): Action {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []): Action {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(): Action {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(): Action {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []): Action {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadTaskListSections(options: EntityOptions = {}) {
  const { entityKey } = options

  // @ts-expect-error TS2314
  return (dispatch: AppDispatch, getState: () => RootReducerState): Promise<ActionResult> => {
    const loadedForKeys = [...getState().taskListSections.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/task_list_sections.json', options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.TASK_LIST_SECTION_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function loadTaskListSection(taskListSectionId: number, options: EntityOptions = {}) {
  // @ts-expect-error TS2314
  return (dispatch: AppDispatch, getState: () => RootReducerState): Promise<ActionResult> => {
    const state = getState()
    const loadedIds = [...state.taskListSections.loadedIds]

    if (taskListSectionId && !loadedIds.includes(taskListSectionId)){
      loadedIds.push(taskListSectionId)
    }

    // Set Loading
    dispatch(fetchRequest({ loadedIds }))

    const promise = api(`/task_list_sections/${taskListSectionId}.json`, options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.TASK_LIST_SECTION)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function createTaskListSection(taskListSection: TaskListSectionModel, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      task_list_section: taskListSection,
    }),
  }

  // @ts-expect-error TS2314
  return (dispatch: AppDispatch): Promise<ActionResult> => {
    dispatch(createRequest())

    const promise = api('/task_list_sections.json', options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.TASK_LIST_SECTION)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function updateTaskListSection(taskListSection: TaskListSectionModel, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      task_list_section: taskListSection,
    }),
  }

  // @ts-expect-error TS2314
  return (dispatch: AppDispatch): Promise<ActionResult> => {
    dispatch(updateRequest())

    const promise = api(`/task_list_sections/${taskListSection.id}.json`, options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.TASK_LIST_SECTION)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function deleteTaskListSection(taskListSection: TaskListSectionModel) {
  const config = {
    method: 'DELETE',
  }

  // @ts-expect-error TS2314
  return (dispatch: AppDispatch): Promise<ActionResult> => {
    dispatch(deleteRequest())

    const promise = api(`/task_list_sections/${taskListSection.id}.json`, {}, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.TASK_LIST_SECTION)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: TaskListSectionModuleState = initialState,
  action: Action = { type: '' },
): TaskListSectionModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
